/**
 * @author       jordan.sookikian@zumper.com (Jordan Sookikian)
 * @copyright    Copyright (c) 2021, Zumper
 */

import { falseReducer, payloadKeyReducer, payloadReducer } from '@zcode/reducers'
import type { Stream } from '@zcode/state-type'
import { combineReducers } from 'redux'
import { type Values, handleAction, handleActions } from 'redux-actions'

import type * as actions from '../actions'
import {
  RECEIVE_STREAM_CHANNELS,
  SET_FETCHED_UNFILTERED_CHANNELS,
  SET_FETCHING_STREAM_CONVERSATIONS,
  SET_FETCHING_UNFILTERED_CHANNELS,
  SET_UNREAD_COUNTS,
} from '../constants/index'
import { selectStreamChannelIdBuildingOnlyKey, selectStreamChannelIdKey } from '../selectors/index'

type Actions = ReturnType<Values<typeof actions>>

export const streamReducer = combineReducers<Stream, Actions>({
  // channels will be stored as lId,bId: {data: {streamChannelId: string}, loading: boolean}
  // ex: { listingId=123,buildingId=456: {data: {streamChannelid: 'asdf-123-456'}, loading: false }
  channelIds: handleActions<Stream['channelIds'], Actions>(
    {
      [RECEIVE_STREAM_CHANNELS]: (state, { payload }) => {
        const newState = { ...state }
        for (const {
          building_id: buildingId,
          listing_id: listingId,
          stream_channel_id: streamChannelId,
        } of payload) {
          const key = selectStreamChannelIdKey(listingId ?? undefined, buildingId ?? undefined)
          if (key) {
            newState[key] = { data: { streamChannelId }, fetching: false }
          }
          if (listingId && buildingId) {
            // If only 1 floor plan of a building has been messaged, there will be both a buildingId
            // and listingId (the floor plan's id) associated with the Stream channel. However, the
            // "Message" button for the building is going to look for `listingId=undefined,buildingId=123`,
            // so we need to store the stream channel id under that key.
            // (For the "Message" button on individual floor plans, we check for
            // `listingId=123,builidingId=456`, so we add it under that key too.)
            const buildingOnlyKey = selectStreamChannelIdKey(undefined, buildingId)
            if (buildingOnlyKey) {
              newState[buildingOnlyKey] = { data: { streamChannelId }, fetching: false }
            }
          }
        }
        return newState
      },
      [SET_FETCHING_STREAM_CONVERSATIONS]: (state, { payload: { fetching, ids } }) => {
        let changed = false
        const newState = { ...state }
        for (const listingAndBuildingId of ids) {
          const key = selectStreamChannelIdKey(...listingAndBuildingId)
          if (key && newState[key]?.fetching !== fetching) {
            changed = true
            newState[key] = { data: null, ...newState[key], fetching }
          }
          const buildingOnlyKey = selectStreamChannelIdBuildingOnlyKey(...listingAndBuildingId)
          if (buildingOnlyKey) {
            if (newState[buildingOnlyKey]?.fetching !== fetching) {
              changed = true
              newState[buildingOnlyKey] = { data: null, ...newState[buildingOnlyKey], fetching }
            }
          }
        }
        return changed ? newState : state
      },
    },
    {}
  ),
  fetchedAllChannels: handleAction<
    Stream['fetchedAllChannels'],
    Actions,
    typeof SET_FETCHED_UNFILTERED_CHANNELS
  >(SET_FETCHED_UNFILTERED_CHANNELS, payloadKeyReducer('fetchedAll'), false),
  fetchedUnfilteredChannelsAt: handleAction<
    Stream['fetchedUnfilteredChannelsAt'],
    Actions,
    typeof SET_FETCHED_UNFILTERED_CHANNELS
  >(
    SET_FETCHED_UNFILTERED_CHANNELS,
    payloadKeyReducer('fetchedAt'),
    null // Date.now() | null
  ),
  fetchingUnfilteredChannels: handleActions<Stream['fetchingUnfilteredChannels'], Actions>(
    {
      [SET_FETCHED_UNFILTERED_CHANNELS]: falseReducer,
      [SET_FETCHING_UNFILTERED_CHANNELS]: payloadReducer,
    },
    false
  ),
  unreadCounts: handleAction<Stream['unreadCounts'], Actions, typeof SET_UNREAD_COUNTS>(
    SET_UNREAD_COUNTS,
    payloadReducer,
    null
  ),
})
